// src/components/SecurityView.tsx
//
// Thin trust page. No SOC 2, SOC 1, SOX, or HIPAA claims.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from './SEO';
import { Button } from './ui/button';

export const SecurityView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Security | Five Dollar Workday"
        description="FDW reads your card processor. We don't hold card numbers, your ledger, or your bank. We don't train models on your program data."
        canonical="https://fivedollarworkday.com/security"
      />

      <main className="relative min-h-screen bg-brand-surface text-brand-black font-sans selection:bg-brand-accent selection:text-white pt-16">

        <section className="px-6 md:px-12 py-16 md:py-20 border-b border-brand-border">
          <div className="max-w-2xl mx-auto flex flex-col gap-6">
            <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray">
              Security
            </p>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08]">
              We read the processor. We don't hold the cards.
            </h1>
            <p className="text-lg text-brand-charcoal font-light leading-relaxed">
              FDW finishes the Marqeta or Lithic night. The pack lives in your workspace.
              Your ledger and your bank stay yours.
            </p>
          </div>
        </section>

        <section className="px-6 md:px-12 py-20 border-b border-brand-border">
          <div className="max-w-2xl mx-auto flex flex-col gap-10">

            <div className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold tracking-tight">What we hold</h2>
              <p className="text-brand-charcoal font-light leading-relaxed">
                Processor events and reports you grant us to read: tokens, amounts, merchants,
                settlement totals. The graphs and packs we build from those. Account email
                for the people who can open the pack.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold tracking-tight">What we don't hold</h2>
              <ul className="text-brand-charcoal font-light leading-relaxed space-y-2 list-none">
                <li>Card numbers, PINs, or security codes.</li>
                <li>Your general ledger or bank login.</li>
                <li>The right to send a wire or post a journal.</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold tracking-tight">How we use models</h2>
              <p className="text-brand-charcoal font-light leading-relaxed">
                We don't train models on your program data. Matching the night is rules on
                processor tokens. Chat can explain a pack you already have. It cannot run
                a close or call the processor to move money.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold tracking-tight">Access</h2>
              <p className="text-brand-charcoal font-light leading-relaxed">
                Processor keys are read-only and scoped to your workspace. People on your
                account can open packs. The desk can open them to answer a question.
                Your own tools may read a finished pack later; they don't run the night job.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold tracking-tight">Reviews</h2>
              <p className="text-brand-charcoal font-light leading-relaxed">
                We do not have a SOC 2 report yet. We will not put a badge on the site
                until a CPA issues one. If you need a questionnaire for a pilot, send it.
                We answer from this page and from how the workspace actually runs.
              </p>
            </div>

          </div>
        </section>

        <section className="px-6 md:px-12 py-24">
          <div className="max-w-2xl mx-auto flex flex-col gap-6">
            <h2 className="text-2xl font-semibold tracking-tight">
              Questions before a map call are fine.
            </h2>
            <p className="text-brand-charcoal font-light">
              Make an account. Tell us which processor. The chat books 30 minutes.
            </p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate('/signup?next=/chat&intent=program-map')}
              className="w-fit"
            >
              Get a program map
            </Button>
          </div>
        </section>

      </main>
    </>
  );
};

export default SecurityView;